/* ==========================================================================
   EcoFlow Malaysia — theme.js
   --------------------------------------------------------------------------
   Everything the theme needs, and nothing it does not. No framework, no
   jQuery, no animation library. Roughly 3 KB before gzip.

   The rule that keeps it that way: a behaviour earns a place here only if it
   cannot be done in CSS or by the platform.
   ========================================================================== */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Reveal ──────────────────────────────────────────────────────────────
     The single motion pattern. One observer for the whole document; elements
     unobserve after firing so nothing accumulates. */

  function initReveal(root) {
    var scope = root || document;

    /* Number the children of every staggered group once, so the CSS can space
       their entrances without a timer per element. */
    scope.querySelectorAll('[data-ef-stagger]').forEach(function (group) {
      var i = 0;
      [].forEach.call(group.children, function (child) {
        child.style.setProperty('--ef-i', i++);
      });
    });

    var els = scope.querySelectorAll('.eft-reveal:not(.is-in), [data-ef-stagger]:not(.is-in)');
    if (!els.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        obs.unobserve(entry.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ── Cart count ──────────────────────────────────────────────────────────
     Read from the JSON island in theme.liquid, then kept current by
     ef:cart:update events that any section can dispatch. */

  function setCartCount(n) {
    document.querySelectorAll('[data-ef-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n < 1;
    });
  }

  function initCart() {
    var node = document.getElementById('ef-cart-state');
    if (node) {
      try { setCartCount(JSON.parse(node.textContent).count || 0); } catch (e) {}
    }
    document.addEventListener('ef:cart:update', function (e) {
      if (e.detail && typeof e.detail.count === 'number') setCartCount(e.detail.count);
    });
  }

  /* Shared helper so sections never hand-roll a fetch to /cart/add.js. */
  window.efAddToCart = function (items) {
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function (added) {
        return fetch(window.Shopify.routes.root + 'cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('ef:cart:update', { detail: cart }));
            document.dispatchEvent(new CustomEvent('ef:cart:added', { detail: cart }));
            return added;
          });
      });
  };

  /* ── Declarative analytics ───────────────────────────────────────────────
     Apple puts data-analytics-title on 340 elements and reads them with one
     listener. Tracking then survives a redesign, because it is attached to
     intent rather than to a CSS selector. */

  function initAnalytics() {
    document.addEventListener('click', function (e) {
      var el = e.target.closest('[data-analytics-title]');
      if (!el) return;
      var payload = {
        link_text: el.getAttribute('data-analytics-title'),
        link_region: el.getAttribute('data-analytics-region') || '',
        link_section: el.getAttribute('data-analytics-section') || ''
      };
      if (typeof window.gtag === 'function') window.gtag('event', 'select_content', payload);
      document.dispatchEvent(new CustomEvent('ef:analytics', { detail: payload }));
    }, { passive: true });
  }

  /* ── Disclosure ──────────────────────────────────────────────────────────
     Menus and accordions. <details> handles most cases; this covers the ones
     that need a button controlling a separate panel. */

  function initDisclosure(root) {
    (root || document).querySelectorAll('[data-ef-toggle]').forEach(function (btn) {
      if (btn.dataset.efBound) return;
      btn.dataset.efBound = '1';
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') {
          btn.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
          btn.focus();
        }
      });
    });
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */

  function init(root) {
    initReveal(root);
    initDisclosure(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); initCart(); initAnalytics(); });
  } else {
    init(); initCart(); initAnalytics();
  }

  /* Theme editor: sections are re-rendered without a page load, so anything
     bound on load has to be re-bound here or the section arrives inert. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    e.target.classList.add('is-in');
  });
})();


/* ── Mega menu ────────────────────────────────────────────────────────────
   Hover opens it on pointers that can hover; click and keyboard work
   everywhere. Hover alone would strand keyboard and touch users, and click
   alone would feel wrong on desktop, so both drive the same show()/hide(). */

(function () {
  var items = [].slice.call(document.querySelectorAll('[data-mega-item]'));
  if (!items.length) return;

  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var open = null;

  var closeTimer = null;
  function cancelClose() { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } }
  function closeSoon(item) { cancelClose(); closeTimer = setTimeout(function () { hide(item); }, 220); }

  function panelOf(item) { return item.querySelector('[data-mega-panel]'); }
  function triggerOf(item) { return item.querySelector('[data-mega-trigger]'); }

  var scrim = document.querySelector('[data-mega-scrim]');

  function show(item) {
    cancelClose();
    if (open === item) return;
    if (open) hide(open);
    var p = panelOf(item), t = triggerOf(item);
    if (!p || !t) return;
    p.hidden = false;
    if (scrim) scrim.hidden = false;
    /* Forced reflow so the growth animates from closed rather than snapping
       open — the same reason the drawer does it. */
    void p.offsetWidth;
    p.classList.add('is-in');
    if (scrim) scrim.classList.add('is-in');
    t.setAttribute('aria-expanded', 'true');
    item.setAttribute('data-open', '');
    open = item;
  }

  function hide(item) {
    var p = panelOf(item), t = triggerOf(item);
    if (t) t.setAttribute('aria-expanded', 'false');
    item.removeAttribute('data-open');
    if (scrim) {
      scrim.classList.remove('is-in');
      setTimeout(function () { if (!open) scrim.hidden = true; }, 340);
    }
    if (p) {
      p.classList.remove('is-in');
      /* Wait for the collapse before removing it from the tree, with a timer
         rather than transitionend so reduced-motion still cleans up. */
      setTimeout(function () { if (!p.classList.contains('is-in')) p.hidden = true; }, 400);
    }
    if (open === item) open = null;
  }

  items.forEach(function (item) {
    var t = triggerOf(item);
    if (!t) return;

    t.addEventListener('click', function (e) {
      var href = t.getAttribute('href');
      if (!href || href === '#') { e.preventDefault(); }
      if (canHover) return;                 /* desktop: let the link navigate */
      if (open !== item) { e.preventDefault(); show(item); }
    });

    /* Keyboard users get the panel on focus, since click now navigates. */
    t.addEventListener('focus', function () { show(item); });

    if (canHover) {
      /* The panel is a DOM child of the item but sits visually below the
         header, so the pointer crosses header padding on its way down. That
         transit leaves the item's box and fired mouseleave instantly, closing
         the panel before it could be reached. A short close delay — cancelled
         the moment the pointer lands anywhere inside the item — bridges it. */
      item.addEventListener('mouseenter', function () { cancelClose(); show(item); });
      item.addEventListener('mouseleave', function () { closeSoon(item); });

      var panel = panelOf(item);
      if (panel) {
        panel.addEventListener('mouseenter', cancelClose);
        panel.addEventListener('mouseleave', function () { closeSoon(item); });
      }
    }

    /* Keyboard: leaving the whole item closes it. focusout fires before the
       next element is focused, so read relatedTarget, not activeElement. */
    item.addEventListener('focusout', function (e) {
      if (!e.relatedTarget || !item.contains(e.relatedTarget)) hide(item);
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !open) return;
    var t = triggerOf(open);
    hide(open);
    if (t) t.focus();
  });

  document.addEventListener('click', function (e) {
    if (open && !open.contains(e.target)) hide(open);
  });

  if (scrim) scrim.addEventListener('click', function () { if (open) hide(open); });
})();

/* ── Mobile nav drill-down ─────────────────────────────────────────────────
   Tapping a category slides the WHOLE drawer to a new screen showing just
   its children, with a back arrow to return — apple.com's own mobile menu
   pattern, replacing the old expand-in-place accordion. Every screen is
   already server-rendered in the DOM; "navigating" is only ever moving which
   one carries .is-active, never a fetch. Only two levels deep (root, and one
   screen per category), so there is no navigation stack to manage — just
   "which single screen is current." */
(function () {
  var containers = document.querySelectorAll('[data-mnav-screens]');
  if (!containers.length) return;

  function bind(container) {
    var panel = container.closest('.eft-drawer__panel');
    var screens = container.querySelectorAll('[data-mnav-screen]');
    var backBtn = panel ? panel.querySelector('[data-mnav-back]') : null;
    if (!screens.length) return;

    function screenFor(key) {
      for (var i = 0; i < screens.length; i++) {
        if (screens[i].getAttribute('data-mnav-screen') === key) return screens[i];
      }
      return null;
    }

    function goTo(key) {
      var target = screenFor(key);
      if (!target) return;
      var root = screenFor('root');

      screens.forEach(function (s) {
        s.classList.remove('is-active', 'is-before');
        s.inert = true;
      });

      if (key !== 'root' && root) root.classList.add('is-before');
      target.classList.add('is-active');
      target.inert = false;
      target.scrollTop = 0;

      if (backBtn) backBtn.hidden = key === 'root';
    }

    container.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-mnav-open]');
      if (opener) goTo(opener.getAttribute('data-mnav-open'));
    });

    if (backBtn) backBtn.addEventListener('click', function () { goTo('root'); });

    /* The drawer itself dispatches this on every open (see drawer.js) — reset
       to the root screen each time, so the menu never reopens deep in a
       category left over from the last visit. */
    var drawer = container.closest('.eft-drawer');
    if (drawer) drawer.addEventListener('ef:drawer:opened', function () { goTo('root'); });
  }

  containers.forEach(bind);
})();

/* ── Rail axis lock ────────────────────────────────────────────────────────
   A horizontal scroller sitting in a vertical page will happily eat a
   trackpad gesture that is mostly vertical but slightly diagonal — the page
   stops moving and the rail creeps sideways, which reads as the page being
   stuck. Decide the axis once per gesture and hold it.

   Only the dominant-vertical case is intercepted; horizontal gestures are
   left entirely alone, so dragging a rail sideways still works. */

(function () {
  var rails = document.querySelectorAll(
    '.eft-clineup, .eft-crail__list, .eft-frail__rail, .eft-reasons__grid, .eft-lineup__rail, .eft-pnav__rail'
  );
  if (!rails.length) return;

  rails.forEach(function (rail) {
    var axis = null, idle;

    rail.addEventListener('wheel', function (e) {
      var x = Math.abs(e.deltaX), y = Math.abs(e.deltaY);

      /* First meaningful movement of a gesture decides the axis. */
      if (!axis) axis = x > y ? 'x' : 'y';

      clearTimeout(idle);
      idle = setTimeout(function () { axis = null; }, 140);

      if (axis === 'y') {
        /* Hand it back to the page: scroll the window ourselves and stop the
           rail from consuming it. */
        e.preventDefault();
        window.scrollBy({ top: e.deltaY, behavior: 'instant' });
      }
    }, { passive: false });

    /* A touch drag is unambiguous, so only the wheel needs this. Reset on
       pointer entry so a new gesture is judged fresh. */
    rail.addEventListener('pointerenter', function () { axis = null; });
  });
})();


/* ==========================================================================
   Product gallery
   --------------------------------------------------------------------------
   There was no gallery code in this theme at all. The thumbnails rendered as
   plain images with nothing bound to them, so clicking one did nothing on
   every product page in the store.

   All frames are in the DOM already, so switching is a class change — no
   fetch, no flash, and the strip works with the keyboard as a real tablist.
   ========================================================================== */
(function () {
  'use strict';

  function bind(root) {
    if (root.dataset.efGalleryBound) return;
    root.dataset.efGalleryBound = '1';

    var frames = root.querySelectorAll('[data-ef-frame]');
    var thumbs = root.querySelectorAll('[data-ef-thumb]');
    if (frames.length < 2 || !thumbs.length) return;

    function show(i) {
      if (i < 0) i = frames.length - 1;
      if (i >= frames.length) i = 0;
      for (var f = 0; f < frames.length; f++) {
        frames[f].classList.toggle('is-current', f === i);
      }
      for (var t = 0; t < thumbs.length; t++) {
        var on = t === i;
        thumbs[t].classList.toggle('is-current', on);
        thumbs[t].setAttribute('aria-selected', on ? 'true' : 'false');
      }
      return i;
    }

    thumbs.forEach(function (b, i) {
      b.addEventListener('click', function () { show(i); });

      /* Arrow keys move along the strip, which is what a tablist is expected
         to do and what a keyboard user will try first. */
      b.addEventListener('keydown', function (e) {
        var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        var n = show(i + d);
        thumbs[n].focus();
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-ef-gallery]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else { init(); }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();


/* ==========================================================================
   Lineup filter chips
   --------------------------------------------------------------------------
   Cards carry the groups they belong to; chips carry a group index. Toggling
   is therefore a hidden attribute, not a fetch — the row keeps its place and
   nothing reflows under the pointer.
   ========================================================================== */
(function () {
  'use strict';

  function bind(bar) {
    if (bar.dataset.efFilterBound) return;
    bar.dataset.efFilterBound = '1';

    var list = bar.parentNode.querySelector('.eft-clineup');
    if (!list) return;
    var cards = list.querySelectorAll('.eft-clineup__card');
    var chips = bar.querySelectorAll('[data-ef-filter]');

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var key = chip.dataset.efFilter;

        chips.forEach(function (o) {
          var on = o === chip;
          o.classList.toggle('is-on', on);
          o.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        cards.forEach(function (card) {
          if (key === 'all') { card.hidden = false; return; }
          var groups = (card.getAttribute('data-ef-groups') || '').split(/\s+/);
          card.hidden = groups.indexOf(key) === -1;
        });

        /* A filtered row that starts mid-scroll looks broken. */
        list.scrollTo({ left: 0, behavior: 'smooth' });
      });
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('.eft-cfilter').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else { init(); }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();


/* ==========================================================================
   Variant selection
   --------------------------------------------------------------------------
   Choosing an option only rewrote a hidden field. The price stayed on whatever
   variant happened to load first and the gallery never moved, so picking EB600
   showed EB300's photo beside EB300's price — on a page where the two differ
   by 286 Wh and RM499.

   Price, stock, the add button and the gallery all follow the selection now.
   ========================================================================== */
(function () {
  'use strict';

  function bind(form) {
    if (form.dataset.efVariantBound) return;
    form.dataset.efVariantBound = '1';

    var root = form.closest('[data-analytics-section="product"]') || document;
    var node = root.querySelector('[data-ef-variants]');
    if (!node) return;

    var variants;
    try { variants = JSON.parse(node.textContent); } catch (e) { return; }
    if (!variants || variants.length < 2) return;

    var selects  = form.querySelectorAll('[data-ef-option]');
    var idField  = form.querySelector('[data-ef-variant-id]');
    var addBtn   = form.querySelector('[data-ef-add-btn]');
    var priceEl  = root.querySelector('[data-ef-price]');
    var gallery  = root.querySelector('[data-ef-gallery]');

    function match() {
      var chosen = [];
      selects.forEach(function (s) { chosen[+s.dataset.efOption] = s.value; });
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i], ok = true;
        for (var k = 0; k < chosen.length; k++) {
          if (chosen[k] !== undefined && v.options[k] !== chosen[k]) { ok = false; break; }
        }
        if (ok) return v;
      }
      return null;
    }

    function showMedia(mediaId) {
      if (!gallery || !mediaId) return;
      var frame = gallery.querySelector('[data-ef-frame][data-ef-media-id="' + mediaId + '"]');
      if (!frame) return;
      var idx = frame.getAttribute('data-ef-frame');
      gallery.querySelectorAll('[data-ef-frame]').forEach(function (f) {
        f.classList.toggle('is-current', f.getAttribute('data-ef-frame') === idx);
      });
      gallery.querySelectorAll('[data-ef-thumb]').forEach(function (b) {
        var on = b.getAttribute('data-ef-thumb') === idx;
        b.classList.toggle('is-current', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        /* Keep the chosen thumbnail in view in the filmstrip. */
        if (on && b.scrollIntoView) b.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }

    function apply() {
      var v = match();
      if (!v) return;
      if (idField) idField.value = v.id;
      if (priceEl) priceEl.textContent = v.price;
      if (addBtn) {
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available
          ? (addBtn.dataset.efLabel || addBtn.textContent)
          : 'Sold out';
      }
      showMedia(v.mediaId);

      /* Deep links and sharing should carry the choice. */
      if (window.history && window.history.replaceState) {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', u);
      }
    }

    selects.forEach(function (s) {
      if (addBtn && !addBtn.dataset.efLabel) addBtn.dataset.efLabel = addBtn.textContent.trim();
      s.addEventListener('change', apply);
    });

    /* Honour ?variant= on load so a shared link opens on the right one. */
    var want = new URL(window.location.href).searchParams.get('variant');
    if (want) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(want)) {
          selects.forEach(function (s) {
            var k = +s.dataset.efOption;
            if (variants[i].options[k] !== undefined) s.value = variants[i].options[k];
          });
          break;
        }
      }
    }
    apply();
  }

  function init(scope) {
    (scope || document).querySelectorAll('form[action*="/cart/add"]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else { init(); }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
